import { useMemo, type ReactNode } from "react";
import { Banner, Flex, FullscreenTakeover, QueryLoader } from "@jsq/ds/core";
import {
  useGetCountriesQuery,
  useGetDocumentExtractedDataQuery,
} from "~/graphql/supergraphHooks";
import { DocumentsViewPanel } from "../../CaseDetailsDrawer/Documents/DocumentManagement/DocumentManagementDisplay/DocumentsViewPanel";
import { checkIsUsCountrySelected } from "../../CaseDetailsDrawer/Profiles/common/AddressFields/StateFormField/utils";
import { findCountryMatch } from "../CaseCreationPreview/countryMatcher";
import { findUsStateMatch } from "../CaseCreationPreview/stateMatcher";
import {
  previewBody,
  previewPanel,
  rightPanel,
  rightPanelWrapper,
} from "../CaseCreationPreview/styles_vanilla.css";

type Props = {
  documentId: number;
  /**
   * Renders the right-panel form, given the normalized extracted data. Lets callers
   * reuse the document viewer + extraction pipeline with their own form.
   */
  children: (extractedData: Record<string, string>) => ReactNode;
};

/**
 * agentExtractedData is a JSON scalar (nullable, values of unknown shape). Keep only
 * string-valued entries so downstream form consumers get a real Record<string, string>.
 */
const toStringRecord = (value: unknown): Record<string, string> => {
  if (value == null || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
};

/**
 * Shared dual-panel preview for AI document extraction: shows the source document on
 * the left and a caller-provided, pre-filled form on the right. Handles fetching the
 * extracted data and normalizing country/state values.
 */
export const ExtractedDataPreviewPanel = ({ documentId, children }: Props) => {
  const documentExtractedData = useGetDocumentExtractedDataQuery({
    variables: { docId: documentId },
    skip: !documentId,
  });
  const extractedData = useMemo(
    () => toStringRecord(documentExtractedData.data?.document?.agentExtractedData),
    [documentExtractedData.data],
  );

  const countriesResult = useGetCountriesQuery({ skip: !documentId });

  const matchedCountry = useMemo(
    () =>
      countriesResult.data
        ? findCountryMatch(extractedData["country"], countriesResult.data.complianceCountries)
        : undefined,
    [extractedData, countriesResult.data],
  );

  const normalizedExtractedData = useMemo(() => {
    const countryCode = matchedCountry?.alpha2code ?? extractedData["country"];
    const rawState = extractedData["state"];
    const normalizedState = checkIsUsCountrySelected(countryCode)
      ? (findUsStateMatch(rawState) ?? "")
      : rawState;

    const merged: Record<string, string | undefined> = {
      ...extractedData,
      country: countryCode,
      state: normalizedState,
    };

    return Object.fromEntries(
      Object.entries(merged).filter(([, value]) => value !== undefined),
    ) as Record<string, string>;
  }, [extractedData, matchedCountry]);

  return (
    <QueryLoader result={[documentExtractedData, countriesResult]}>
      <FullscreenTakeover.Body className={previewBody}>
        <Flex className={previewPanel}>
          <DocumentsViewPanel documentId={documentId} />
        </Flex>
        <Flex className={rightPanelWrapper}>
          <Flex className={rightPanel}>
            <Banner
              variant="success"
              icon="circle-bolt"
              title="We've filled in the data from your document. Please review and fill in any missing information."
            />
            {children(normalizedExtractedData)}
          </Flex>
        </Flex>
      </FullscreenTakeover.Body>
    </QueryLoader>
  );
};
